// Portable workflows on the front page: what they are, beside the featured workflow playing
// itself on the canvas.
import { ArrowRight, DownloadSimple, GitBranch, SealCheck } from "@phosphor-icons/react";
import { Link } from "react-router";

import { useWorkflow, useWorkflows } from "../../api/hooks";
import { SectionHead } from "../../ui/Section";
import { WorkflowPlayer } from "../../workflows/LazyPlayer";

const POINTS = [
  { Icon: GitBranch, text: "Steps, branches, guards and human approvals in one open document" },
  { Icon: SealCheck, text: "Versioned and immutable once published: read it before you run it" },
  { Icon: DownloadSimple, text: "Download it, review it, run it anywhere that speaks the format" },
];

function FeaturedPlayer() {
  const list = useWorkflows();
  const slug = list.data?.workflows[0]?.slug;
  const page = useWorkflow(slug ?? "");
  if (!slug || !page.data) return <div className="h-full animate-pulse rounded-2xl bg-paper2" aria-hidden />;
  const { workflow, latest } = page.data;
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
        <div className="min-w-0">
          <p className="truncate font-semibold">{workflow.name}</p>
          <p className="font-mono text-[11px] uppercase tracking-wider text-ink3">
            v{latest.version} · {workflow.step_count} steps · {workflow.publisher}
          </p>
        </div>
        <Link to={`/workflows/${workflow.slug}`} className="btn-line shrink-0 !px-3.5 !py-1.5 text-[13px]">
          Open
        </Link>
      </div>
      <WorkflowPlayer graph={latest.document.version.definition} demoPath={latest.demo_path} className="min-h-0 flex-1" compact />
    </div>
  );
}

export function WorkflowFeature() {
  return (
    <section className="border-y border-line bg-paper2/50">
      <div className="wrap grid gap-12 py-20 sm:py-28 lg:grid-cols-[1fr_1.35fr] lg:items-center">
        <div>
          <SectionHead kicker="§ Portable workflows" title={<>Automation you can open before you run.</>}>
            A portable workflow is an OCS idea for the whole world: one open document that says exactly what an AI automation will do, step by step, and who has to say yes.
          </SectionHead>
          <ul className="mt-8 space-y-4">
            {POINTS.map((p) => (
              <li key={p.text} className="flex gap-3 text-[15.5px] text-ink2">
                <p.Icon weight="duotone" className="mt-0.5 h-5 w-5 shrink-0 text-signal" aria-hidden /> {p.text}
              </li>
            ))}
          </ul>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/workflows" className="btn-ink">
              Browse the marketplace <ArrowRight weight="bold" className="h-4 w-4" />
            </Link>
            <Link to="/portable-workflows" className="btn-line">
              Read the spec
            </Link>
          </div>
        </div>
        <div className="card h-[460px] overflow-hidden sm:h-[520px]">
          <FeaturedPlayer />
        </div>
      </div>
    </section>
  );
}
